import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Facebook, Youtube, Mail, Phone, MapPin } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";
import client from "@/lib/api";
import { toast } from "sonner";

export default function Footer() {
    const { settings } = useSettings();
    const [email, setEmail] = useState("");
    const subscribe = async (e) => {
        e.preventDefault();
        if (!email) return;
        try {
            await client.post("/enquiries", { type: "newsletter", name: "Newsletter", phone: "-", email, message: "Newsletter subscription" });
            toast.success("Subscribed! We'll keep you in the loop.");
            setEmail("");
        } catch { toast.error("Could not subscribe. Try again."); }
    };
    return (
        <footer className="bg-[color:var(--warm-black)] text-[color:var(--ivory)] mt-24" data-testid="site-footer">
            {/* Newsletter */}
            <div className="border-b border-white/10">
                <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-14 grid md:grid-cols-2 gap-8 items-center">
                    <div>
                        <div className="eyebrow text-[color:var(--champagne)]">Stay in Touch</div>
                        <h3 className="font-serif-luxe text-3xl md:text-4xl mt-3 leading-tight">Be first to see our latest collections.</h3>
                        <p className="text-sm text-[color:var(--ivory)]/60 mt-3 max-w-md">Occasional emails on new arrivals, bridal previews, festive edits and gold rate updates. No noise, only sparkle.</p>
                    </div>
                    <form onSubmit={subscribe} className="flex flex-col sm:flex-row gap-3" data-testid="newsletter-form">
                        <input type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="Your email" className="flex-1 bg-transparent border border-white/20 focus:border-[color:var(--champagne)] outline-none px-4 py-3 text-sm placeholder:text-[color:var(--ivory)]/40" data-testid="newsletter-email" />
                        <button className="btn-gold" data-testid="newsletter-submit">Subscribe</button>
                    </form>
                </div>
            </div>

            <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-16 grid grid-cols-2 lg:grid-cols-5 gap-10">
                <div className="col-span-2">
                    <div className="flex items-center gap-3 mb-4">
                        <img src="/logo.png" alt="Singh Jewellers" className="h-14 w-auto" />
                        <div className="font-accent tracking-[0.32em] text-xl">SINGH<span className="text-[color:var(--champagne)] mx-1">·</span>JEWELLERS</div>
                    </div>
                    <p className="text-sm text-[color:var(--ivory)]/70 font-sans-body leading-relaxed mb-6 max-w-md">
                        Premium gold, silver, diamond and bridal jewellery. Timeless craftsmanship at honest, competitive making charges — proudly serving the families of Kotdwar and beyond.
                    </p>
                    <div className="space-y-2 text-sm text-[color:var(--ivory)]/80">
                        <div className="flex items-start gap-2"><MapPin size={14} className="mt-0.5 text-[color:var(--champagne)]" />{settings.address || "Malini Market, Jhanda Chowk, Kotdwar, Uttarakhand 246149"}</div>
                        <div className="flex items-center gap-2"><Phone size={14} className="text-[color:var(--champagne)]" />{settings.phone || "+91 99999 99999"}</div>
                        <div className="flex items-center gap-2"><Mail size={14} className="text-[color:var(--champagne)]" />{settings.email || "info@singhjewellers.com"}</div>
                    </div>
                </div>
                <div>
                    <div className="eyebrow text-[color:var(--champagne)] mb-4">Quick Links</div>
                    <ul className="space-y-2 text-sm text-[color:var(--ivory)]/80">
                        <li><Link to="/gold-jewellery">Gold Jewellery</Link></li>
                        <li><Link to="/silver-jewellery">Silver Jewellery</Link></li>
                        <li><Link to="/diamond-jewellery">Diamond Jewellery</Link></li>
                        <li><Link to="/bridal-jewellery">Bridal</Link></li>
                        <li><Link to="/new-arrivals">New Arrivals</Link></li>
                    </ul>
                </div>
                <div>
                    <div className="eyebrow text-[color:var(--champagne)] mb-4">Explore</div>
                    <ul className="space-y-2 text-sm text-[color:var(--ivory)]/80">
                        <li><Link to="/gold-harvest">Gold Harvest Plan</Link></li>
                        <li><Link to="/kitty-plan">Kitty Plan</Link></li>
                        <li><Link to="/gold-rates">Gold & Silver Rates</Link></li>
                        <li><Link to="/making-charges">Making Charges Guide</Link></li>
                        <li><Link to="/custom-jewellery">Custom Jewellery</Link></li>
                        <li><Link to="/blog">Jewellery Guide</Link></li>
                    </ul>
                </div>
                <div>
                    <div className="eyebrow text-[color:var(--champagne)] mb-4">Support</div>
                    <ul className="space-y-2 text-sm text-[color:var(--ivory)]/80">
                        <li><Link to="/contact">Contact</Link></li>
                        <li><Link to="/about">About Us</Link></li>
                        <li><Link to="/privacy-policy">Privacy Policy</Link></li>
                        <li><Link to="/terms">Terms</Link></li>
                    </ul>
                    <div className="flex gap-3 mt-5">
                        <a href="#" aria-label="Instagram" className="text-[color:var(--champagne)]"><Instagram size={18} /></a>
                        <a href="#" aria-label="Facebook" className="text-[color:var(--champagne)]"><Facebook size={18} /></a>
                        <a href="#" aria-label="YouTube" className="text-[color:var(--champagne)]"><Youtube size={18} /></a>
                    </div>
                </div>
            </div>
            <div className="border-t border-white/10">
                <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-5 text-xs text-[color:var(--ivory)]/50 flex flex-wrap items-center justify-between gap-3">
                    <span>© {new Date().getFullYear()} SINGH JEWELLERS. All Rights Reserved.</span>
                    <span>Crafted with heritage for Kotdwar, Uttarakhand.</span>
                </div>
            </div>
        </footer>
    );
}
